/**
 * chart-feed — the app-side bridge between the transport layer's columnar data
 * and the chart engine's per-plot `SeriesDelta`s.
 *
 * Transport is rendering-agnostic: it emits columns keyed by *column name*
 * (`open`/`high`/`low`/`close`/`volume` on the base frame, plus one column per
 * requested indicator alias) and, for realtime, per-column deltas. The chart,
 * however, is keyed by each plot's `outputAlias` and a candlestick plot wants a
 * single OHLC delta. This module maps between the two, which is exactly the
 * "consumer turns ColumnDelta into its own SeriesDelta" seam the transport docs
 * describe — and the seam where the live QvWebSocketClient later drops in.
 */

import type { ChartGroup, Indicator } from "@qufolio/model";
import type { SeriesDelta } from "@qufolio/chart-engine";
import { hasOhlc, type ChartGroupColumns, type ColumnDelta } from "@qufolio/transport";
import { pipFrequencyFor } from "./pip";
import { membersForIndex } from "./topdown";

/** Column names the transport always emits on the base price frame. */
const BASE_COLUMNS = new Set(["open", "high", "low", "close", "volume"]);

/**
 * QV's bundle envelope encodes ChartGroupId as a uint32; our contract ids are
 * ULIDs. Hash the ULID (FNV-1a) to a stable uint32 so the server can echo it
 * back and the client can correlate the response.
 */
export function chartGroupIdInt(ulid: string): number {
  let h = 2166136261;
  for (let i = 0; i < ulid.length; i++) {
    h ^= ulid.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}


/** The OHLC fields a candle needs, in the order a delta carries them. */
const OHLC_FIELDS = ["open", "high", "low", "close"] as const;

/**
 * The four column aliases a foreign candle is fed by, or `undefined`.
 *
 * A candlestick plot normally requests **nothing**: it reads the base
 * `open/high/low/close` every price frame already carries. A candle of *another*
 * symbol has no such base — those columns are this chart's symbol — so it has to
 * ask for four columns of its own and be fed from them.
 *
 * The alias spells that with `ohlc` where a single-field plot names its field:
 * `ohlc@AAPL` expands to `open@AAPL`…`close@AAPL`, and `ohlc/ohlc@SPY` to
 * `open/open@SPY`… — substituting every occurrence, which is what keeps both
 * sides of a Compare-to ratio moving together.
 */
function foreignCandleColumns(alias: string): Record<string, string> | undefined {
  if (!alias.includes("@") || !alias.includes("ohlc")) return undefined;
  const columns: Record<string, string> = {};
  for (const field of OHLC_FIELDS) columns[field] = alias.replaceAll("ohlc", field);
  return columns;
}

/**
 * The four columns a **derived** bar set is fed by — `haOhlc` → `haOpen`…`haClose`.
 *
 * A bar source that keeps one bar per bar — Heikin-Ashi is the first — ships its
 * prices alongside the real ones rather than replacing them, so the chart can
 * draw HA candles *and* a real close line from one frame. The plot picks which
 * by its alias, and the alias needs expanding to four columns exactly as a
 * foreign candle's does.
 *
 * **Without this an HA plot would silently draw real candles.** `isOhlc` is
 * decided by the plot's *type* alone; an OHLC plot with no column map requests
 * nothing and is handed the base frame's `open/high/low/close` whatever it is
 * called. That is the right default for `ohlc` and quietly wrong for anything
 * else, so every non-base candle alias has to resolve to a map here.
 *
 * `<prefix>Ohlc` rather than a fixed list, so the next 1:1 transform needs no
 * change. Plain `ohlc` is deliberately not matched — the capital is what keeps
 * the base case falling through to the base columns.
 */
function derivedCandleColumns(alias: string): Record<string, string> | undefined {
  if (!alias.endsWith("Ohlc")) return undefined;
  const prefix = alias.slice(0, -"Ohlc".length);
  if (!prefix) return undefined;
  const columns: Record<string, string> = {};
  for (const field of OHLC_FIELDS) {
    columns[field] = prefix + field[0]!.toUpperCase() + field.slice(1);
  }
  return columns;
}

/**
 * The four columns a candle plot reads, or `undefined` for the base frame's own.
 *
 * One door for both kinds, because all three feed paths have to agree: a plot
 * whose request is built from one rule and whose data is matched by another is
 * a plot that asks for columns nobody reads.
 */
function candleColumnsFor(alias: string): Record<string, string> | undefined {
  return foreignCandleColumns(alias) ?? derivedCandleColumns(alias);
}

/** An alias that names bars rather than a column: `ohlc`, `haOhlc`, `ohlc@SPY`. */
function isPriceAlias(alias: string): boolean {
  return alias === "ohlc" || candleColumnsFor(alias) !== undefined;
}

interface PlotFeed {
  alias: string;
  isOhlc: boolean;
  // Mutable, because `plotFeeds` merges every plot reading this alias into one
  // feed — see its note.
  /**
   * The second series a `band` plot fills against — Bollinger's lower edge
   * under its upper.
   *
   * A band is the one renderable fed by **two** columns (`plan/series.ts` maps
   * it to an `XyyDataSeries`), so it is the one plot whose alias does not
   * describe its whole request. Every consumer below has to handle the pair or
   * the fill arrives with one edge and draws against zero — a shape that
   * renders confidently and is nothing like the indicator.
   */
  alias2?: string;
}

/**
 * One feed per **column**, not per plot — and that distinction is the whole
 * reason catalog band fills drew nothing.
 *
 * Several plots can read one alias. A catalog band is drawn *under* the lines it
 * spans and shares an edge with one of them: Bollinger's fill and its upper edge
 * are both `bbandsUpper_20_2`, Chandelier's long fill and its long stop are both
 * `ceLong_22_3_1`. Emitting a feed per plot therefore produced two deltas on one
 * alias, and the renderer keys its lookup by alias — so the second silently
 * replaced the first, the fill (registered first, being ordered below) never
 * received a bar, and a band rendered as nothing at all.
 *
 * Merged, one delta carries every column any plot on that alias needs, and
 * `appendData` dispatches on each plot's *own* `dataKind` — so the band takes
 * `y` and `high` while the line beside it takes `y` and ignores the rest. That
 * is also why the two must not be split back apart: handing the band the line's
 * delta would leave `high` empty, and `XyyDataSeries.append` **defaults** the
 * missing edge to zero rather than skipping the point, which floods the pane.
 *
 * The limit, stated because it is silent: one alias carries one `alias2`. Two
 * *different* fills sharing a primary edge cannot be expressed, and nothing
 * builds that today — the catalog gives each band its own primary (`ceLong`,
 * `ceShort`) and `addBandBetween` mints from two plots the user picked.
 *
 * **One plot type contributes no feed at all**, and it is the other end of the
 * same thought. A `volumeByPrice` plot's numbers are not in a column: its bins
 * are computed on the client (`chart-engine`'s `plan/volume-by-price.ts`) from
 * the closes and volumes the candles already loaded, because a dozen rows of
 * `(zone, up, down)` whose edges are themselves derived from the data is not
 * something `chart_indicators.compute` — one `pl.Expr` per bar — can say. It
 * still carries an alias, since that is what names the family and its parameters
 * through a reload and what `editIndicatorParams` rewrites, so the skip has to
 * key on the **type**. Emitting a feed for it would put a name qv-data cannot
 * answer into the request of every chart carrying one, and an unanswered alias
 * is silence rather than an error — the chart would draw perfectly while asking
 * for something that does not exist, which is why this is a test and not a
 * comment alone.
 */
function plotFeeds(group: ChartGroup): PlotFeed[] {
  const feeds = new Map<string, PlotFeed>();
  for (const pane of group.panes) {
    for (const plot of pane.plots ?? []) {
      if (!plot.outputAlias) continue;
      // Computed on the client from bars this chart already holds — see the
      // note above. Not an oversight, and not a plot that draws nothing.
      if (plot.type === "volumeByPrice") continue;
      // **Price by its alias as well as its type** (2026-10-03): the chart-type
      // control turns the price plot into a `line` or `mountain` and keeps its
      // `ohlc` alias, and decided by type alone that became an indicator named
      // `ohlc` — asked of a server that has no such column, so the line drew
      // nothing. An OHLC alias always reads the bars; the plot takes `y` (the
      // close) from them when it is not a candle.
      const isOhlc =
        plot.type === "candlestick" || plot.type === "ohlc" || isPriceAlias(plot.outputAlias);
      const alias2 = plot.type === "band" ? plot.outputAlias2 : undefined;
      const found = feeds.get(plot.outputAlias);
      if (found) {
        if (isOhlc) found.isOhlc = true;
        if (alias2 && !found.alias2) found.alias2 = alias2;
        continue;
      }
      feeds.set(plot.outputAlias, {
        alias: plot.outputAlias,
        isOhlc,
        ...(alias2 ? { alias2 } : {}),
      });
    }
  }
  return [...feeds.values()];
}

/**
 * The suffix that mints the PIP's own chart-group id.
 *
 * The PIP rides the **same socket and the same request** as the chart it sits
 * in — `IndicatorRequest.chartGroups` has always been a list and qv-data has
 * always looped it, so a second timeframe costs one more entry rather than a
 * second connection. What it does cost is an id the response can be told apart
 * by, and hashing `<ulid>:pip` gives one that is stable, derived, and stored
 * nowhere.
 */
export const PIP_ID_SUFFIX = ":pip";

/** The uint32 the server will echo back for this group's PIP. */
export function pipChartGroupIdInt(ulid: string): number {
  return chartGroupIdInt(ulid + PIP_ID_SUFFIX);
}

/**
 * How many bars the PIP loads.
 *
 * Its own number, not the chart's: `barsRange` defaults to 1500, and 1500
 * *weekly* bars is nearly thirty years — which no server holds and no
 * 180-pixel panel could draw. Enough for a couple of years at the coarsest
 * rung, which is what the panel is for: the shape of the timeframe above, not
 * its history.
 */
export const PIP_BARS = 260;

/** One chart group on the wire, as this module builds them. */
interface GroupRequest {
  chartGroupId: number;
  symbol: string;
  frequency: string;
  barsRange?: number;
  barSource?: string;
  projectForward?: boolean;
  /** A TopDown index's members, beside its `~TDe.<hash>` symbol (`lib/topdown.ts`). */
  indexMembers?: string[];
  indicators: Indicator[];
}

/**
 * The PIP's entry in the request, or `undefined` when there is nothing to ask
 * for.
 *
 * **Only the `chart` view is a subscription.** The other five are HTTP fetches
 * keyed by symbol, so they must not reach the socket at all — asking for a chart
 * group in order to render a profile would load bars nobody draws.
 *
 * **It inherits `barSource`**, and that is not a detail. The PIP is a higher
 * timeframe *of this chart*, so a Heikin-Ashi chart with a real-candle weekly in
 * the corner would be two different instruments drawn as one thought. The
 * chart's own answer is the panel's answer.
 *
 * `indicators: []` because a PIP draws candles and nothing else, and it shares
 * the symbol by construction — the panel is bound to its chart group, so there
 * is no second symbol to keep in step.
 */
function pipRequestFor(group: ChartGroup): GroupRequest | undefined {
  if (group.pipView !== "chart") return undefined;
  // Resolved rather than read: a stored interval the chart has since caught up
  // with is promoted here, so the request never asks for the timeframe already
  // on screen. `null` means the chart is at the top of the ladder and there is
  // no higher timeframe to show.
  const frequency = pipFrequencyFor(group.frequency, group.pipFrequency);
  if (!frequency) return undefined;
  return {
    chartGroupId: pipChartGroupIdInt(group.id),
    symbol: group.symbol,
    frequency,
    barsRange: PIP_BARS,
    ...(group.barSource !== undefined ? { barSource: group.barSource } : {}),
    ...indexMembersFor(group.symbol),
    indicators: [],
  };
}

/**
 * Build the transport request for a chart group. We only request the *computed*
 * indicator aliases (non-OHLC plots whose alias isn't a base OHLCV column) —
 * OHLC and volume come from the base price frame every group carries.
 *
 * `barsRange` rides along, and its absence here is what made the field dead for
 * as long as it existed: the server has honoured it since it stopped sending
 * everyone 500 bars, but nothing was putting it on the wire, so a chart group
 * asking for 1500 still got the default. It is the *loaded* count — the visible
 * window is the client's own business and never leaves the browser.
 */
export function toIndicatorRequest(
  group: ChartGroup,
  layoutId: string,
  userId: string | number,
): {
  layoutId: string;
  userId: string | number;
  chartGroups: GroupRequest[];
} {
  const seen = new Set<string>();
  const indicators: Indicator[] = [];
  const request = (column: string) => {
    if (BASE_COLUMNS.has(column) || seen.has(column)) return;
    seen.add(column);
    indicators.push({ shortname: column, name: column, aliases: [column] });
  };

  for (const { alias, isOhlc, alias2 } of plotFeeds(group)) {
    // A foreign candle is the one OHLC plot that *does* need a request: its
    // four columns are another symbol's and are not in the base frame.
    const foreign = isOhlc ? candleColumnsFor(alias) : undefined;
    if (foreign) {
      for (const column of Object.values(foreign)) request(column);
      continue;
    }
    if (isOhlc) continue;
    request(alias);
    // A band's second edge is a column of its own and would otherwise never be
    // asked for — the fill would then draw one series against nothing.
    if (alias2) request(alias2);
  }
  const pip = pipRequestFor(group);
  return {
    layoutId,
    userId,
    chartGroups: [
      {
        chartGroupId: chartGroupIdInt(group.id),
        symbol: group.symbol,
        frequency: group.frequency,
        ...(group.barsRange !== undefined ? { barsRange: group.barsRange } : {}),
        // **Without this the server computes every indicator on real bars.**
        // The HA candles would still draw — `haOhlc` fetches its four columns
        // through the ordinary alias path, which does not depend on this — so
        // the chart looks right while its overlays are read off prices it is not
        // showing. That is the exact failure the whole bar-source seam exists to
        // avoid, arrived at by forgetting one line.
        ...(group.barSource !== undefined ? { barSource: group.barSource } : {}),
        // **Without this the server computes no projected bars**, and every
        // `~fwd` alias below is asked for and answered with nothing — a plot
        // that renders as an empty series rather than as an error. The aliases
        // alone are not the request: they say *which* twins, and this says
        // whether there are any.
        //
        // Exactly the shape `barsRange` failed in — two halves, each shipped
        // believing the other did it. Pinned by a test on the flag itself,
        // because a test on the aliases passes either way.
        ...(group.projectForward ? { projectForward: true } : {}),
        // A TopDown index is named by a hash of its members, and the server
        // builds it from the list sent here (`docs/features/topdown.md`).
        ...indexMembersFor(group.symbol),
        indicators,
      },
      // Second, never first: `ChartGroupView` reads `chartGroups[0]` to list
      // what it asked the server for, so a PIP that pushed the chart's own entry
      // down would make the data inspector describe the wrong group.
      ...(pip ? [pip] : []),
    ],
  };
}

/** `{ indexMembers }` for a TopDown index symbol this page has named; else nothing. */
function indexMembersFor(symbol: string): { indexMembers?: string[] } {
  const members = membersForIndex(symbol);
  return members ? { indexMembers: members } : {};
}

/**
 * The four column names a PIP's candles come from.
 *
 * A Heikin-Ashi chart's PIP asks for `barSource: "heikinAshi"`, and the server
 * answers with the derived prices in their own columns *beside* the real ones —
 * the whole point of that seam. So the panel has to pick, and picking wrong is
 * silent: the base columns are always present, so an HA PIP reading them draws
 * ordinary candles at the right timeframe, which looks entirely correct.
 */
function pipColumns(barSource: ChartGroup["barSource"]): Record<string, string> {
  return barSource === "heikinAshi"
    ? { open: "haOpen", high: "haHigh", low: "haLow", close: "haClose" }
    : { open: "open", high: "high", low: "low", close: "close" };
}

/** One bar of the PIP's own timeframe. */
export interface PipBar {
  open: number;
  high: number;
  low: number;
  close: number;
}

/**
 * The PIP's bars from a decoded snapshot, or `[]` when the frame cannot supply
 * them.
 *
 * All four columns or none, `bulkDeltas`' rule for a foreign candle and for its
 * reason: a candle missing a side draws a bar with an invented edge, and it
 * reads as real.
 */
export function pipBars(cols: ChartGroupColumns, barSource: ChartGroup["barSource"]): PipBar[] {
  const names = pipColumns(barSource);
  const o = cols.columns[names["open"]!];
  const h = cols.columns[names["high"]!];
  const l = cols.columns[names["low"]!];
  const c = cols.columns[names["close"]!];
  if (!o || !h || !l || !c) return [];
  const n = Math.min(o.length, h.length, l.length, c.length);
  const out: PipBar[] = [];
  for (let i = 0; i < n; i++) {
    out.push({ open: o[i]!, high: h[i]!, low: l[i]!, close: c[i]! });
  }
  return out;
}

/**
 * A realtime frame applied to the PIP's bars.
 *
 * Returns a new array rather than mutating, because the panel is a React child
 * and an array mutated in place is an array React has no reason to re-render
 * for.
 *
 * **A patch and an append are different events and both arrive here.** On a
 * weekly PIP the common one by far is `patchLast`: every trading day of the week
 * revises the same bar. Treating a patch as an append would grow one weekly
 * candle into five daily-sized ones.
 */
export function applyPipDeltas(
  bars: readonly PipBar[],
  deltas: readonly ColumnDelta[],
  barSource: ChartGroup["barSource"],
): PipBar[] {
  const names = pipColumns(barSource);
  const by = new Map(deltas.map((d) => [d.alias, d]));
  const field = (k: "open" | "high" | "low" | "close") => by.get(names[k]!);
  const o = field("open");
  const h = field("high");
  const l = field("low");
  const c = field("close");
  // The close is the one that must be present: it is the only column every
  // renderable path treats as required, and a frame without it says nothing
  // about a bar.
  if (!c) return bars as PipBar[];

  let out = bars as PipBar[];
  const appended = c.append?.y;
  if (appended?.length) {
    const add: PipBar[] = [];
    for (let i = 0; i < appended.length; i++) {
      const close = appended[i]!;
      add.push({
        open: o?.append?.y?.[i] ?? close,
        high: h?.append?.y?.[i] ?? close,
        low: l?.append?.y?.[i] ?? close,
        close,
      });
    }
    out = [...out, ...add];
  }
  if (c.patchLast?.y !== undefined && out.length > 0) {
    const last = out[out.length - 1]!;
    out = [
      ...out.slice(0, -1),
      {
        open: o?.patchLast?.y ?? last.open,
        high: h?.patchLast?.y ?? last.high,
        low: l?.patchLast?.y ?? last.low,
        close: c.patchLast.y,
      },
    ];
  }
  return out;
}

/**
 * How many leading rows of a frame are real bars.
 *
 * A frame can now run past its last bar: a seasonal projection is drawn on
 * dates the symbol has no bar for, and those rows arrive with every OHLCV
 * column null — `NaN` once decoded. The count is the index of the last finite
 * `close`, plus one.
 *
 * Derived rather than sent, matching `diffChartGroup`'s settled edge: the fact
 * is already in the data, and a second statement of it in the envelope is a
 * second thing that can disagree.
 *
 * A frame with no `close` at all is entirely real, which is what keeps the
 * offline fixture and any indicator-only frame behaving as they always did.
 */
export function realBarCount(cols: ChartGroupColumns): number {
  const close = cols.columns["close"];
  if (!close) return cols.x.length;
  for (let i = close.length - 1; i >= 0; i--) {
    if (Number.isFinite(close[i])) return i + 1;
  }
  return 0;
}

/**
 * Bulk load: one `SeriesDelta` per column from a decoded snapshot.
 *
 * **Candles stop at the last real bar; every other series keeps its full
 * length.** The asymmetry is deliberate and each half has a different reason.
 *
 * A candle series must not hold points past the last bar, because the *next*
 * real bar arrives as an append at a **lower** x than those points — the
 * projection is months out and tomorrow is one day out — and a series whose x
 * goes backwards is one SciChart draws wrong rather than refuses.
 *
 * Everything else keeps its NaN tail, because `plan/y-fit.ts` reads series by
 * **offset into their arrays**: the Y fit converts the window to an index range
 * once and applies it to every candidate. A short array in the middle of the
 * set would be read at the wrong bars. One that starts at index 0 and simply
 * ends early is safe — `fitYRange` clamps its end — which is exactly the shape
 * a trimmed candle series has.
 */
export function bulkDeltas(cols: ChartGroupColumns, group: ChartGroup): SeriesDelta[] {
  const out: SeriesDelta[] = [];
  const x = cols.x;
  const real = realBarCount(cols);
  /** `values` cut to the real bars, or unchanged when nothing is projected. */
  const bars = (values: number[]): number[] =>
    real < values.length ? values.slice(0, real) : values;
  for (const { alias, isOhlc, alias2 } of plotFeeds(group)) {
    if (alias2) {
      // `XyyDataSeries.append(x, y, y1)` — the shell reads `y` and `high`, so
      // the primary alias is one edge and the second is the other.
      //
      // **The pair drops to a single column rather than to nothing** when the
      // second edge is absent, because this alias also feeds the *line* along
      // that edge and it must still draw. A band handed one edge fills it
      // against zero, so declining is the band's own job and is done where the
      // series' shape is known — see `updateSeries`, which skips an xyy plot on
      // a delta with no `high`.
      const upper = cols.columns[alias];
      const lower = cols.columns[alias2];
      if (upper) {
        out.push({ alias, append: lower ? { x, y: upper, high: lower } : { x, y: upper } });
      }
      continue;
    }
    const foreign = isOhlc ? candleColumnsFor(alias) : undefined;
    if (foreign) {
      const close = cols.columns[foreign["close"]!];
      // All four or none: a candle missing a side draws a bar with an invented
      // edge, which reads as real. The server omits a symbol it has no bars
      // for, so this is the shape that arrives when one is not held.
      if (close && OHLC_FIELDS.every((f) => cols.columns[foreign[f]!])) {
        out.push({
          alias,
          append: {
            x: bars(x),
            y: bars(close),
            open: bars(cols.columns[foreign["open"]!]!),
            high: bars(cols.columns[foreign["high"]!]!),
            low: bars(cols.columns[foreign["low"]!]!),
            close: bars(close),
          },
        });
      }
      continue;
    }
    if (isOhlc && hasOhlc(cols)) {
      out.push({
        alias,
        append: {
          x: bars(x),
          y: bars(cols.columns["close"]!),
          open: bars(cols.columns["open"]!),
          high: bars(cols.columns["high"]!),
          low: bars(cols.columns["low"]!),
          close: bars(cols.columns["close"]!),
        },
      });
    } else {
      const y = cols.columns[alias];
      if (y) out.push({ alias, append: { x, y } });
    }
  }
  return out;
}

/**
 * Realtime frame: transport delivers per-column deltas; re-key them onto the
 * chart's plots, reassembling the base OHLC columns into one candle delta.
 */
export function appendDeltas(deltas: ColumnDelta[], group: ChartGroup): SeriesDelta[] {
  const byColumn = new Map(deltas.map((d) => [d.alias, d]));
  const out: SeriesDelta[] = [];

  for (const { alias, isOhlc, alias2 } of plotFeeds(group)) {
    if (alias2) {
      // Falls back to the primary column alone when the second edge did not
      // tick, for `bulkDeltas`' reason: this alias also feeds the line along
      // that edge, and `updateSeries` is where an xyy plot declines an unpaired
      // delta.
      const band = bandFromColumns(byColumn, alias, alias2);
      const single = band ?? byColumn.get(alias);
      if (single) out.push({ ...single, alias });
    } else if (isOhlc) {
      const foreign = candleColumnsFor(alias);
      const candle = foreign
        ? candleFromColumns(byColumn, alias, foreign)
        : candleFromColumns(byColumn, alias);
      if (candle) out.push(candle);
    } else {
      const d = byColumn.get(alias);
      if (d) out.push({ ...d, alias }); // ColumnDelta is SeriesDelta-shaped
    }
  }
  return out;
}

/**
 * Combine a band's two edge deltas into one `xyy` SeriesDelta.
 *
 * Both edges or nothing. The two columns are computed by the same expression
 * over the same bars, so they arrive together or the frame is malformed — and
 * emitting a half-band would append rows to an `XyyDataSeries` whose second
 * value silently defaults, putting the fill's far edge on zero.
 */
function bandFromColumns(
  byColumn: Map<string, ColumnDelta>,
  alias: string,
  alias2: string,
): SeriesDelta | undefined {
  const upper = byColumn.get(alias);
  const lower = byColumn.get(alias2);
  if (!upper || !lower) return undefined;

  if (upper.append && lower.append) {
    return {
      alias,
      append: { x: upper.append.x, y: upper.append.y, high: lower.append.y },
    };
  }
  if (upper.patchLast && lower.patchLast) {
    return { alias, patchLast: { y: upper.patchLast.y, high: lower.patchLast.y } };
  }
  return undefined;
}

/** Combine open/high/low/close column deltas into one candle SeriesDelta. */
function candleFromColumns(
  byColumn: Map<string, ColumnDelta>,
  alias: string,
  /** Field -> column name, for a candle fed by another symbol's columns. */
  columns?: Record<string, string>,
): SeriesDelta | undefined {
  const named = (field: string) => byColumn.get(columns ? columns[field]! : field);
  const c = named("close");
  if (!c) return undefined;
  const o = named("open");
  const h = named("high");
  const l = named("low");

  const sd: SeriesDelta = { alias };
  if (c.append) {
    sd.append = {
      x: c.append.x,
      y: c.append.y,
      open: o?.append?.y,
      high: h?.append?.y,
      low: l?.append?.y,
      close: c.append.y,
    };
  }
  if (c.patchLast) {
    sd.patchLast = {
      // `y` as well: a line or mountain price plot reads it, and without it
      // the live bar never moved on a line chart.
      y: c.patchLast.y,
      close: c.patchLast.y,
      open: o?.patchLast?.y,
      high: h?.patchLast?.y,
      low: l?.patchLast?.y,
    };
  }
  return sd;
}
